import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { NetworkQuality } from 'pure-js/types/AppAssistant'
import {
  getNetworkInformation,
  hasSeenSlowNetworkNotice,
  markSlowNetworkNoticeSeen,
  measureNetworkQuality,
  resolveConnectionSensitiveArea
} from '../libs/clientPerformance'

/** Measured after the first render settles, so page load itself is not what we time. */
const FIRST_MEASUREMENT_DELAY_MS = 8000 // 8 s

/**
 * One non-blocking notice per browser tab when the link looks too slow to play or talk on. Seen once,
 * it never returns for the rest of the visit, whichever page raised it.
 *
 * What the area changes is when the link is RE-MEASURED. The container never unmounts, so a single
 * measurement at app start was the only one that ever ran outside a browser `change` event — and two of
 * the three major browsers have no Network Information API to fire one. A link that degrades before the
 * game or the debrief was therefore never noticed on the pages where it matters most.
 */
export const useSlowNetworkNotice = () => {
  const { pathname } = useLocation()
  const area = resolveConnectionSensitiveArea(pathname)
  const [isVisible, setIsVisible] = useState(false)

  const dismiss = useCallback(() => {
    markSlowNetworkNoticeSeen()
    setIsVisible(false)
  }, [])

  useEffect(() => {
    if (hasSeenSlowNetworkNotice()) return undefined

    let isMounted = true

    const measureOnce = async () => {
      if (!isMounted || hasSeenSlowNetworkNotice()) return
      const sample = await measureNetworkQuality()
      if (!isMounted || sample.quality !== NetworkQuality.SLOW) return

      markSlowNetworkNoticeSeen()
      setIsVisible(true)
    }

    const firstMeasurement = setTimeout(measureOnce, FIRST_MEASUREMENT_DELAY_MS)
    const connection = getNetworkInformation()
    connection?.addEventListener?.('change', measureOnce)

    return () => {
      isMounted = false
      clearTimeout(firstMeasurement)
      connection?.removeEventListener?.('change', measureOnce)
    }
  }, [area])

  return { isSlowNetworkNoticeVisible: isVisible, dismissSlowNetworkNotice: dismiss }
}
